import { ImgID } from "../../image_loader/img_id.js";
import { NetworkManager } from "../../network_manager.js";
import { ColorScheme } from "../common_ui_elements/color_scheme.js";
import { TextStyleScheme } from "../common_ui_elements/text_style_scheme.js";
import { UIImage } from "../common_ui_elements/ui_image.js";
import { UINineSliceElement } from "../common_ui_elements/ui_nine_slice_element.js";
import { MyTextAlign, MyTextVerticalAlign, UITextElement } from "../common_ui_elements/ui_text_element.js";
import { AbstractDialog } from "./abstract_dialog.js";
import { DialogId } from "./dialog_id.js";
import { DialogManager } from "./dialog_manager.js";

export type ConnectionFailedVariant = "connection" | "loading_error" | "offline_levels_exhausted" | "offline_leaderboard";

const VARIANT_KEYS: Record<ConnectionFailedVariant, { title: string; text: string }> = {
  connection: { title: "CONNECTION.FAILED", text: "CONNECTION.MAIN_TEXT" },
  loading_error: { title: "LOADING_ERROR.TITLE", text: "LOADING_ERROR.TEXT" },
  offline_levels_exhausted: { title: "OFFLINE.LEVELS_EXHAUSTED.TITLE", text: "OFFLINE.LEVELS_EXHAUSTED.TEXT" },
  offline_leaderboard: { title: "OFFLINE.LEADERBOARD.TITLE", text: "OFFLINE.LEADERBOARD.TEXT" },
};

export class ConnectionFailedDialog extends AbstractDialog
{
  public static readonly dialog_id = DialogId.CONNECTION_FAILED_DIALOG;

  private static readonly DESIGN_W = 906;
  private static readonly DESIGN_H = 820;
  private static readonly PANEL_Y = 120;
  private static readonly PANEL_H = 700;

  private title_text!: UITextElement;
  private message_text!: UITextElement;

  constructor( private variant: ConnectionFailedVariant = "connection" )
  {
    const body_w = LayoutScaler.widthFromDesign( ConnectionFailedDialog.DESIGN_W );
    const body_h = LayoutScaler.heightFromDesign( ConnectionFailedDialog.DESIGN_H );
    const body_x = ( DialogManager.getWidth() - body_w ) / 2;
    const body_y = ( DialogManager.getHeight() - body_h ) / 2;

    super( DialogManager.ctx, body_x, body_y, body_w, body_h, false );

    this.createBackground();
    this.createStatusBadge();
    this.createTitle();
    this.createMessage();
    this.createReconnectButton();
    this.createCloseButton();
  }

  private createBackground(): void
  {
    this.addChild( new UINineSliceElement(
      this.ctx,
      0,
      LayoutScaler.yFromDesign( ConnectionFailedDialog.PANEL_Y ),
      this.rect.getW(),
      LayoutScaler.heightFromDesign( ConnectionFailedDialog.PANEL_H ),
      ImgID.UI_CapsuleBlue,
      "capsule_bg"
    ) );
  }

  private createStatusBadge(): void
  {
    const badge_size = LayoutScaler.widthFromDesign( 287 );
    const badge_x = ( this.rect.getW() - badge_size ) / 2;
    const badge_y = LayoutScaler.yFromDesign( -5 );
    this.addChild( new UIImage(
      this.ctx,
      badge_x,
      badge_y,
      badge_size,
      badge_size,
      ImgID.AvatarCircle
    ) );

    const icon_size = LayoutScaler.widthFromDesign( 245 );
    const icon_x = ( this.rect.getW() - icon_size ) / 2;
    const icon_y = LayoutScaler.yFromDesign( 14 );
    this.addChild( new UIImage(
      this.ctx,
      icon_x,
      icon_y,
      icon_size,
      icon_size,
      ImgID.UI_ConnectionFailedIcon
    ) );
  }

  private createTitle(): void
  {
    this.title_text = new UITextElement(
      this.ctx,
      0,
      LayoutScaler.yFromDesign( 245 ),
      this.rect.getW(),
      LayoutScaler.heightFromDesign( 92 ),
      {
        localization_key: VARIANT_KEYS[this.variant].title,
        font_size: LayoutScaler.textSizeFromDesign( 60 ),
        text_style: TextStyleScheme.geologica,
        text_color: ColorScheme.white,
        text_align: MyTextAlign.Center,
        vertical_align: MyTextVerticalAlign.Center,
        font_weight: 900,
        auto_wrap: false,
        scale_to_width: true,
        outline_width: LayoutScaler.textBorderSizeFromDesign( 12 ),
        outline_color: ColorScheme.menu_top_el_text,
        shadow_blur: LayoutScaler.widthFromDesign( 5 ),
        shadow_color: ColorScheme.black_transparent,
        shadow_offset_y: LayoutScaler.yFromDesign( 3 ),
      }
    );
    this.addChild( this.title_text );
  }

  private createMessage(): void
  {
    const text_w = LayoutScaler.widthFromDesign( 770 );
    this.message_text = new UITextElement(
      this.ctx,
      ( this.rect.getW() - text_w ) / 2,
      LayoutScaler.yFromDesign( 350 ),
      text_w,
      LayoutScaler.heightFromDesign( 175 ),
      {
        localization_key: VARIANT_KEYS[this.variant].text,
        font_size: LayoutScaler.textSizeFromDesign( 45 ),
        text_style: TextStyleScheme.geologica,
        text_color: ColorScheme.white,
        text_align: MyTextAlign.Center,
        vertical_align: MyTextVerticalAlign.Center,
        font_weight: 900,
        auto_wrap: true,
        line_height: 1.0,
        outline_width: LayoutScaler.textBorderSizeFromDesign( 8 ),
        outline_color: ColorScheme.menu_top_el_text,
      }
    );
    this.addChild( this.message_text );
  }

  private createReconnectButton(): void
  {
    const button_w = LayoutScaler.widthFromDesign( 790 );
    const button_h = LayoutScaler.heightFromDesign( 230 );
    const button = new UINineSliceElement(
      this.ctx,
      ( this.rect.getW() - button_w ) / 2,
      LayoutScaler.yFromDesign( 555 ),
      button_w,
      button_h,
      ImgID.UI_GreenFrameBtn,
      "frame_btn",
      undefined,
      undefined,
      true
    );

    button.addChild( new UITextElement(
      this.ctx,
      0,
      LayoutScaler.yFromDesign( 5 ),
      button_w,
      button_h,
      {
        localization_key: "RECONNECT",
        font_size: LayoutScaler.textSizeFromDesign( 58 ),
        text_style: TextStyleScheme.geologica,
        text_color: ColorScheme.white,
        text_align: MyTextAlign.Center,
        vertical_align: MyTextVerticalAlign.Center,
        font_weight: 900,
        ...TextStyleScheme.buttonShadow(),
        outline_color: ColorScheme.more_lives_buy_outline,
        outline_width: LayoutScaler.textBorderSizeFromDesign( 8 ),
        outline_offset_y: LayoutScaler.yFromDesign( 2 ),
      }
    ), true );

    button.setOnClick( () =>
    {
      void NetworkManager.retryByUser();
    } );

    this.addChild( button );
  }

  private createCloseButton(): void
  {
    const size = LayoutScaler.widthFromDesign( 114 );
    const close_button = new UIImage(
      this.ctx,
      this.rect.getW() - size + LayoutScaler.xFromDesign( 10 ),
      LayoutScaler.yFromDesign( 35 ),
      size,
      size,
      ImgID.UI_SettingsDialogCloseBtn,
      {},
      true
    );
    close_button.setOnClick( () => this.close() );
    this.addChild( close_button );
  }

  public render(): void
  {
    if ( !this.shouldBeRendered() ) return;
    this.startRender();
    this.finishRender();
  }

  public override onDialogWillClose(): void
  {
    NetworkManager.onDialogClosed( this );
  }

  public override isPoolable(): boolean { return true; }

  public override onReuse( variant: ConnectionFailedVariant ): void
  {
    this.variant = variant;
    this.title_text.setLocalizationKey( VARIANT_KEYS[variant].title );
    this.message_text.setLocalizationKey( VARIANT_KEYS[variant].text );
  }

  public name(): string { return "ConnectionFailedDialog"; }
}
